import React, { useEffect, useState } from 'react';
import './Play.css';
import XIcon from '../components/XIcon';
import OIcon from '../components/OIcon';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/solid';

import { AnimatePresence, motion } from 'framer-motion';
import { Node, Tree } from '../services/minimax';

export type Side = 'x' | 'o';

let tree: Tree;

function Play() {
    let { side } = useParams<{ side: Side }>() as { side: Side };
    const [turn, setTurn] = useState<Side>('x');
    const [won, setWon] = useState<Side | null | undefined>(undefined);
    const [xWins, setXWins] = useState(0);
    const [oWins, setOWins] = useState(0);
    const [board, setBoard] = useState<(Side | null)[]>([
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
    ]);
    const AISide = side === 'x' ? 'o' : 'x';

    useEffect(() => {
        tree = new Tree(
            new Node([null, null, null, null, null, null, null, null, null]),
            AISide
        );
        tree.generate('x');
        reset();
    }, [side]);

    function toggleTurn() {
        setTurn((turn) => (turn === 'x' ? 'o' : 'x'));
    }

    function handleCellClick(cellIndex: number) {
        if (board[cellIndex] !== null) return;
        if (won !== undefined) return;
        if (turn !== side) return;

        let newBoard = board.map((cell, index) => {
            return index === cellIndex ? turn : cell;
        });

        setBoard(newBoard);
        tree.play(newBoard);
        newBoard = tree.nextAIMove()?.state;
        if (newBoard !== undefined) {
            setBoard(newBoard);
            tree.play(newBoard);
        }
    }

    function handleEndGame(wonSide: Side | null) {
        if (wonSide === null) {
            setWon(null);
            return;
        }

        setWon(wonSide);
        if (wonSide === 'x') {
            setXWins(xWins + 1);
        } else if (wonSide === 'o') {
            setOWins(oWins + 1);
        }
    }

    function checkWinCondition() {
        // check rows for a win condition
        for (let i = 0; i < 9; i += 3) {
            if (
                board[i] === board[i + 1] &&
                board[i] === board[i + 2] &&
                board[i + 1] === board[i + 2] &&
                board[i] !== null
            ) {
                handleEndGame(board[i] as Side);
            }
        }

        // check columns for a win condition
        for (let i = 0; i < 3; i++) {
            if (
                board[i] === board[i + 3] &&
                board[i] === board[i + 6] &&
                board[i + 3] === board[i + 6] &&
                board[i] !== null
            ) {
                handleEndGame(board[i] as Side);
            }
        }

        // check both diagonals for a win condition
        if (
            board[0] === board[4] &&
            board[0] === board[8] &&
            board[4] === board[8] &&
            board[0] !== null
        ) {
            handleEndGame(board[0] as Side);
        }
        if (
            board[2] === board[4] &&
            board[2] === board[6] &&
            board[4] === board[6] &&
            board[2] !== null
        ) {
            handleEndGame(board[2] as Side);
        }

        if (board.every((cell) => cell !== null)) {
            handleEndGame(null);
        }
    }

    useEffect(() => {
        checkWinCondition();
    }, [board]);

    function cellElement(type: Side | null) {
        if (type === null) return false;

        if (type === 'x') return <XIcon className="h-full w-full" />;

        if (type === 'o') return <OIcon className="h-full w-full" />;
    }

    function reset() {
        setWon(undefined);
        setBoard([null, null, null, null, null, null, null, null, null]);
        setTurn('x');
        tree.reset();

        if (AISide === 'x') {
            let newBoard = tree.nextAIMove()?.state;
            if (newBoard !== undefined) {
                setBoard(newBoard);
                tree.play(newBoard);
                toggleTurn();
            }
        }
    }

    return (
        <>
            <header className="absolute inset-x-0 top-0 flex justify-center">
                <div className="relative flex h-32 w-80 items-center justify-center">
                    <Link to="/" className="absolute left-0 h-8 w-8">
                        <ChevronLeftIcon className="text-gray-500 duration-200 hover:text-gray-700" />
                    </Link>
                    <h1 className="text-4xl font-bold">Tic Tac Toe</h1>
                </div>
            </header>

            <div className="m-auto flex h-screen w-80 flex-col items-center justify-center">
                <div className="w-full">
                    <div className="w-100 flex items-center justify-between text-xl font-bold">
                        <XIcon className="h-8 w-8" />
                        <span>
                            {xWins} - {oWins}
                        </span>
                        <OIcon className="h-8 w-8" />
                    </div>
                </div>
                <div className="relative mt-12 flex flex h-80 w-80 flex-wrap items-center justify-center rounded-xl bg-white shadow-lg">
                    <div className="absolute left-1/3 h-64 w-0.5 rounded-full bg-gray-300"></div>
                    <div className="absolute left-2/3 h-64 w-0.5 rounded-full bg-gray-300"></div>

                    <div className="absolute top-1/3 h-0.5 w-64 rounded-full bg-gray-300"></div>
                    <div className="absolute top-2/3 h-0.5 w-64 rounded-full bg-gray-300"></div>

                    {board.map((cell, i) => (
                        <div
                            key={i}
                            className="no-highlights flex h-1/3 w-1/3 cursor-pointer p-6"
                            onClick={(e) => handleCellClick(i)}
                        >
                            <AnimatePresence>
                                {cell && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{
                                            ease: 'easeIn',
                                            duration: 0.2,
                                        }}
                                        className="h-full w-full"
                                    >
                                        {cellElement(cell)}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}

                    <AnimatePresence>
                        {(won === null || won) && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0 }}
                                className="absolute flex rounded-2xl border-2 border-gray-200 bg-gray-100 bg-opacity-90 px-12 py-4 text-2xl font-bold shadow-lg"
                            >
                                {won !== null ? (
                                    <>
                                        <div className="h-8 w-8">
                                            {cellElement(won)}
                                        </div>
                                        <span className="ml-4">WON!</span>
                                    </>
                                ) : (
                                    <>
                                        <div className="h-8 w-8">
                                            {cellElement('x')}
                                        </div>
                                        <span className="ml-4">TIE!</span>
                                        <div className="ml-4 h-8 w-8">
                                            {cellElement('o')}
                                        </div>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <button
                    className={`mt-24 rounded-full bg-blue-500 py-2 px-6 text-white shadow-xl transition duration-100 hover:bg-blue-400 focus:bg-blue-600 disabled:cursor-not-allowed disabled:bg-blue-300`}
                    disabled={!won && won !== null}
                    onClick={reset}
                >
                    Play Again
                </button>
            </div>
        </>
    );
}

export default Play;
